import {
  UseSuspenseQueryResult,
  type QueryFunction,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useMutation, useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import * as API from '../../api/index.js';
import type {
  DataFileClientStepNames,
  DataFileContext,
  DataFilePreview,
} from '../../api/types.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import { useEnhancedMutation, useTypedQuery } from '../../hooks/index.js';
import {
  createServerStepsConfig,
  getDataFileClientStep,
  getIsDataFileContextFilled,
  getPreviewAfterStepChange,
} from '../../utils/index.js';
import { QueryKeys } from '../queryClient.js';

type QueryOptionsV1 = ReturnType<
  typeof DataIngestion.DataFile.getTableDataFilePreviewV1QueryOptions
>;
type QueryOptionsV2 = ReturnType<
  typeof DataIngestion.DataFile.getTableDataFilePreviewV2QueryOptions
>;

export function useDataFilePreviewQueryOptions<
  V extends 'V1' | 'V2' = 'V1' | 'V2',
>(params: {
  urlParams: { id: string };
}): V extends 'V2' ? QueryOptionsV2 : QueryOptionsV1 {
  const isImportHubV2 = useImportHubV2();

  const queryOptions = isImportHubV2
    ? DataIngestion.DataFile.getTableDataFilePreviewV2QueryOptions(params)
    : DataIngestion.DataFile.getTableDataFilePreviewV1QueryOptions(params);

  return queryOptions as V extends 'V2' ? QueryOptionsV2 : QueryOptionsV1;
}

type DataFilePreviewV1 =
  DataIngestion.DataFile.TableDataFilePreviewV1QueryResult;
type DataFilePreviewV2 =
  DataIngestion.DataFile.TableDataFilePreviewV2QueryResult;

export function useDataFilePreview<V extends 'V1' | 'V2' = 'V1' | 'V2'>(
  dataFileId: string,
): UseSuspenseQueryResult<
  V extends 'V2' ? DataFilePreviewV2 : DataFilePreviewV1,
  Error
> {
  const isImportHubV2 = useImportHubV2();

  const queryOptions = isImportHubV2
    ? DataIngestion.DataFile.getTableDataFilePreviewV2QueryOptions({
        urlParams: { id: dataFileId },
      })
    : DataIngestion.DataFile.getTableDataFilePreviewV1QueryOptions({
        urlParams: { id: dataFileId },
      });

  const { queryKey, queryFn } = queryOptions;

  return useTypedQuery({
    queryKey,
    queryFn: queryFn as QueryFunction<DataFilePreviewV1 | DataFilePreviewV2>,
    /* NOTE: this is the only sensitive query in import hub that should not be refetched automatically */
    suspense: true as const,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  }) as UseSuspenseQueryResult<
    V extends 'V2' ? DataFilePreviewV2 : DataFilePreviewV1,
    Error
  >;
}

export const useUpdateDataFilePreview = (dataFileId: string) => {
  const queryClient = useQueryClient();
  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useMutation({
    mutationFn: () => Promise.resolve(),
    onMutate: async (dataFilePreviewPartial: Partial<DataFilePreview>) => {
      queryClient.setQueryData<DataFilePreview>(
        previewQueryKey,
        (previousPreview) => ({
          ...(previousPreview as DataFilePreview),
          ...dataFilePreviewPartial,
        }),
      );
    },
  });
};

export const useDataFileClientStep = (
  dataFileId: string,
): DataFileClientStepNames => {
  const { data: dataFilePreview } = useDataFilePreview(dataFileId);

  return getDataFileClientStep(dataFilePreview);
};

export const useUpdateCurrentStep = (dataFileId: string) => {
  const { t } = useTranslation('@myn/import-hub');
  const queryClient = useQueryClient();
  const serverStepsConfig = useDataFileServerStepsConfig(dataFileId);

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  const mutationFn = (direction: number) => {
    const isStepForward = direction > 0;
    return isStepForward
      ? API.goToNextStep(dataFileId)
      : API.goToPrevStep(dataFileId);
  };

  return useMutation({
    mutationFn,
    onSuccess: async (_, direction) => {
      const dataFilePreview =
        queryClient.getQueryData<DataFilePreview>(previewQueryKey);

      if (!dataFilePreview) {
        return;
      }

      const isStepForward = direction > 0;
      const isStepBackward = !isStepForward;
      const isRevalidationCall = isStepForward && dataFilePreview.isEdited;
      const nextServerCurrentStep = serverStepsConfig.findStep(
        dataFilePreview.currentStep,
        direction,
      );

      if (isRevalidationCall) {
        queryClient.setQueryData<DataFilePreview>(
          previewQueryKey,
          (prevState) => ({
            ...(prevState as DataFilePreview),
            status: 'validating',
            isEdited: false,
          }),
        );

        queryClient.removeQueries({
          queryKey:
            QueryKeys.dataFile.steps[dataFilePreview.currentStep].base(
              dataFileId,
            ),
          exact: false,
        });

        return;
      }

      const updatedPreview = getPreviewAfterStepChange({
        preview: dataFilePreview,
        nextStep: nextServerCurrentStep.name,
        isStepBackward,
      });

      queryClient.setQueryData(previewQueryKey, updatedPreview);

      if (isStepForward) {
        if (
          nextServerCurrentStep.name === 'validation' ||
          nextServerCurrentStep.name === 'channel_assignment'
        ) {
          queryClient.setQueryData<DataFilePreview>(
            previewQueryKey,
            (prevState) => ({
              ...(prevState as DataFilePreview),
              status: 'validating',
            }),
          );
        }
      }

      if (isStepBackward) {
        queryClient.removeQueries({
          predicate(query) {
            return (
              query.queryKey.includes(dataFileId) &&
              query.queryKey.includes(dataFilePreview.currentStep)
            );
          },
          exact: false,
        });
      }
    },
    onError: (err, direction) => {
      enqueueSnackbar({
        title:
          direction === 1
            ? t('Failed to go to the next step')
            : t('Failed to go to the previous step'),
        variant: 'error',
        removable: true,
      });
    },
  });
};

export function useIsDataFileContextFilled(dataFileId: string) {
  const { data: dataFilePreview } = useDataFilePreview(dataFileId);
  const isImportHubV2 = useImportHubV2();

  const isPricingItemLevel =
    dataFilePreview.granularityLevel === 'pricing_item';

  return getIsDataFileContextFilled({
    context: dataFilePreview.context,
    isImportHubV2,
    isPricingItemLevel,
  });
}

export const useUpdateDataFileContext = (dataFileId: string) => {
  const queryClient = useQueryClient();
  const isImportHubV2 = useImportHubV2();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  const { queryKey: headersMappingQueryKey } =
    DataIngestion.DataFile.getHeadersMappingQueryOptions({
      urlParams: { id: dataFileId },
    });

  return useEnhancedMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DataFileContext }) => {
      const updateEndpoint = isImportHubV2
        ? DataIngestion.DataFile.updateTableDataFileContextV2
        : DataIngestion.DataFile.updateTableDataFileContext;

      return updateEndpoint({
        urlParams: { id },
        data: { context: payload },
      });
    },
    onMutate: async ({ payload }) => {
      await queryClient.cancelQueries({
        queryKey: previewQueryKey,
      });

      const previousValue = queryClient.getQueryData(previewQueryKey);

      if (previousValue) {
        queryClient.setQueryData(previewQueryKey, {
          ...previousValue,
          context: payload,
        });
      }

      return { previousValue };
    },
    onSuccess: (_, vars) => {
      const preview =
        queryClient.getQueryData<DataFilePreview>(previewQueryKey);
      const isPricingItemLevel = preview?.granularityLevel === 'pricing_item';

      const isDataFileContextFilled = getIsDataFileContextFilled({
        context: vars.payload,
        isImportHubV2,
        isPricingItemLevel,
      });

      if (isImportHubV2 && !isPricingItemLevel && isDataFileContextFilled) {
        queryClient.removeQueries({
          queryKey: headersMappingQueryKey,
        });
      }
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(previewQueryKey, context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: previewQueryKey,
      });
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to update context field'),
        };
      },
    },
  });
};

export const useDataFileServerStepsConfig = (dataFileId: string) => {
  const { t } = useTranslation('@myn/import-hub');
  const ff = useFlags('@myn/secured', ['externalDataFeature']);
  const {
    data: { granularityLevel },
  } = useDataFilePreview(dataFileId);

  return useMemo(
    () =>
      createServerStepsConfig({
        t,
        granularityLevel,
        isExternalData: ff.externalDataFeature.enabled,
      }),
    [t, granularityLevel, ff.externalDataFeature.enabled],
  );
};
